import { useMemo, useState } from 'react';

import {
  type ClinicalDataPartial,
  ClinicalDataRibbon,
} from '@/features/sessions/editor/ClinicalDataRibbon';
import {
  type AutosaveState,
  ClinicalEditorShell,
  type EditorDocument,
} from '@/features/sessions/editor/ClinicalEditorShell';
import { EditorFooter } from '@/features/sessions/editor/EditorFooter';
import { EditorTopbar } from '@/features/sessions/editor/EditorTopbar';
import { hasPermission } from '@/lib/rbac/hasPermission';
import { clinicalBreadcrumb } from '@/lib/telemetry/clinical';
import { toast } from '@/lib/toast';
import { useAuthStore } from '@/stores/authStore';

import { aiDraftToEditorContent } from '../editor/aiDraftToEditorContent';
import { useCreateSessionNote, useSubmitSessionNote, useUpdateSessionNote } from '../hooks';
import { canAuthorSessionNote } from '../lib/artifactPermissions';
import type { SessionNote } from '../types/schemas';

import { AiDraftBanner } from './journal/AiDraftBanner';
import { SealRitualDialog } from './journal/SealRitualDialog';

interface Props {
  sessionId: string;
  /** Id of the practitioner who handled this session. Used to gate editing to
   *  the session's own practitioner (plus elevated roles) — mirrors the BE
   *  ownership check so a non-owner reaching this route by URL is read-only. */
  practitionerId: string;
  // Null on anonymous helpline sessions; the back-link then omits the client.
  clientId: string | null;
  clientName: string;
  scheduledStart: string | null;
  authorName: string;
  note: SessionNote | null;
  onSubmitted: (note: SessionNote) => void;
}

interface PrimaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

interface NoteTopbarProps {
  sessionId: string;
  // Null on anonymous helpline sessions; the back-link then omits the client.
  clientId: string | null;
  clientName: string;
  scheduledStart: string | null;
  authorName: string;
  status: 'draft' | 'submitted';
  statusLabel: string;
  statusTone: 'info' | 'neutral';
  autosaveState: AutosaveState;
  wordCount: number;
  primaryAction: PrimaryAction | undefined;
  showAiBanner: boolean;
}

function NoteTopbar({
  sessionId,
  clientId,
  clientName,
  scheduledStart,
  authorName,
  status,
  statusLabel,
  statusTone,
  autosaveState,
  wordCount,
  primaryAction,
  showAiBanner,
}: NoteTopbarProps) {
  return (
    <div className="flex flex-col">
      <EditorTopbar
        kind="note"
        clientName={clientName}
        scheduledStart={scheduledStart}
        status={status}
        statusTone={statusTone}
        statusLabel={statusLabel}
        authorLabel={authorName}
        autosaveState={autosaveState}
        wordCount={wordCount}
        primaryAction={primaryAction}
        backTo={{ sessionId, clientId, drawerTab: 'notes' }}
      />
      {showAiBanner ? (
        <div className="border-accent-ink/10 border-b bg-white px-4 py-2.5">
          <AiDraftBanner showing={showAiBanner} />
        </div>
      ) : null}
    </div>
  );
}

interface NoteFooterProps {
  autosaveState: AutosaveState;
  wordCount: number;
  characterCount: number;
  focusMode: boolean;
  onToggleFocus: () => void;
  primaryAction: PrimaryAction | undefined;
}

function NoteFooter({
  autosaveState,
  wordCount,
  characterCount,
  focusMode,
  onToggleFocus,
  primaryAction,
}: NoteFooterProps) {
  return (
    <EditorFooter
      autosaveState={autosaveState}
      wordCount={wordCount}
      characterCount={characterCount}
      focusMode={focusMode}
      onToggleFocus={onToggleFocus}
      primaryAction={primaryAction}
    />
  );
}

/**
 * Practitioner journal page rebuilt on `ClinicalEditorShell` (D1-D7). The
 * shell owns the prose surface, autosave debounce, focus mode, bubble
 * toolbar and shortcut overlay; this component composes it with the
 * note-specific topbar / footer / side ribbon and wires in the seal-ritual
 * submit flow.
 *
 * The prose autosave path goes through `onAutosave` — when no note exists
 * yet, the first autosave creates the row; subsequent autosaves PATCH it.
 * Structured clinical data (the ribbon) flushes on each field change once
 * a note row exists, mirroring the pre-rewrite behaviour. Telemetry
 * breadcrumbs (`note:create`, `note:update`, `note:submit`) are preserved.
 */
export function SessionNoteEditor({
  sessionId,
  practitionerId,
  clientId,
  clientName,
  scheduledStart,
  authorName,
  note,
  onSubmitted,
}: Props) {
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? null;
  // Capability (role holds the permission) AND ownership (this user may author
  // THIS session's note per the BE guard). Both are required to edit; a
  // non-owner who reached the route by URL sees a read-only surface.
  const canEdit =
    role !== null &&
    (hasPermission(role, 'session_note:create') || hasPermission(role, 'session_note:update')) &&
    canAuthorSessionNote(user, practitionerId);
  const canSubmit =
    role !== null &&
    hasPermission(role, 'session_note:create') &&
    canAuthorSessionNote(user, practitionerId);

  const create = useCreateSessionNote(sessionId);
  const update = useUpdateSessionNote(sessionId);
  const submit = useSubmitSessionNote(sessionId);

  const [sealOpen, setSealOpen] = useState(false);
  // Mirror the pre-rewrite "AI-drafted" auto-dismiss-on-edit behaviour: the
  // banner reads "this is AI-generated, edit it freely" and disappears the
  // moment the practitioner's keystrokes flow through autosave.
  const [proseEditedSinceArrival, setProseEditedSinceArrival] = useState(false);

  const isSubmitted = note?.status === 'submitted';
  const isReadOnly = !canEdit || isSubmitted;

  const onAutosave = async (content: string): Promise<void> => {
    setProseEditedSinceArrival(true);
    try {
      if (note === null) {
        // BE schema is `z.record(z.unknown()).optional()` — `optional()`
        // accepts `undefined` but NOT `null`. Omit the key so Zod treats
        // it as absent (clinicalData is freeform JSONB on the BE; the
        // ribbon will PATCH it later when the practitioner edits a field).
        await create.mutateAsync({ content });
        clinicalBreadcrumb('note:create', { sessionId });
        return;
      }
      await update.mutateAsync({ content });
      clinicalBreadcrumb('note:update', { sessionId });
    } catch (err) {
      toast.saveFailed();
      throw err;
    }
  };

  const onClinicalDataUpdate = (next: ClinicalDataPartial): void => {
    // Don't POST a note that has no prose yet just because the practitioner
    // poked the mood slider — wait for the prose autosave to create the
    // record, then this debounced PATCH path takes over.
    if (note === null) return;
    void update
      .mutateAsync({ clinicalData: next })
      .then(() => {
        clinicalBreadcrumb('note:update', { sessionId });
      })
      .catch(() => {
        toast.saveFailed();
      });
  };

  const onSubmitClick = (): void => {
    if (note === null) {
      toast.error('Save the note before sealing.');
      return;
    }
    setSealOpen(true);
  };

  const onSealConfirm = async (): Promise<void> => {
    if (note === null) return;
    try {
      const submitted = await submit.mutateAsync();
      clinicalBreadcrumb('note:submit', { sessionId });
      toast.noteSubmitted();
      setSealOpen(false);
      onSubmitted(submitted);
    } catch {
      toast.noteSubmitFailed();
    }
  };

  const status = note?.status ?? 'draft';
  const statusLabel = isSubmitted ? 'Submitted' : 'Draft';
  const statusTone: 'info' | 'neutral' = isSubmitted ? 'info' : 'neutral';

  const document = useMemo<EditorDocument | null>(() => {
    if (note === null) return null;
    return {
      id: note.id,
      // SessionNote uses `.nullish()` so these can be `undefined`; the shell
      // contract expects `string | null` — narrow at the boundary. Also
      // convert AI-drafted NDJSON to ProseMirror-friendly HTML on the way in
      // (idempotent for already-HTML content).
      content: aiDraftToEditorContent(note.content),
      status: note.status,
      updatedAt: note.updatedAt ?? null,
    };
  }, [note]);

  const primaryAction: PrimaryAction | undefined =
    canSubmit && !isSubmitted && note !== null
      ? {
          label: 'Seal note',
          onClick: onSubmitClick,
          disabled: submit.isPending,
        }
      : undefined;

  const showAiBanner =
    !isSubmitted &&
    note?.transcriptJobId !== null &&
    note?.transcriptJobId !== undefined &&
    !proseEditedSinceArrival;

  return (
    <>
      {/* `topbar` and `footer` are intentional render-prop slots on the shell
          (they receive shell ctx — autosave state, word count, focus toggle).
          The rule misreads them as inline component definitions; we hand off
          to stable top-level components (NoteTopbar / NoteFooter) so React
          reconciliation is not actually impacted. */}
      <ClinicalEditorShell
        kind="note"
        document={document}
        isReadOnly={isReadOnly}
        onAutosave={onAutosave}
        documentMeta={{ clientName, scheduledStart, authorLabel: authorName }}
        // eslint-disable-next-line react/no-unstable-nested-components
        topbar={(ctx) => (
          <NoteTopbar
            sessionId={sessionId}
            clientId={clientId}
            clientName={clientName}
            scheduledStart={scheduledStart}
            authorName={authorName}
            status={status}
            statusLabel={statusLabel}
            statusTone={statusTone}
            autosaveState={ctx.autosaveState}
            wordCount={ctx.wordCount}
            primaryAction={primaryAction}
            showAiBanner={showAiBanner}
          />
        )}
        // eslint-disable-next-line react/no-unstable-nested-components
        footer={(ctx) => (
          <NoteFooter
            autosaveState={ctx.autosaveState}
            wordCount={ctx.wordCount}
            characterCount={ctx.characterCount}
            focusMode={ctx.focusMode}
            onToggleFocus={ctx.toggleFocusMode}
            primaryAction={primaryAction}
          />
        )}
        sidePanel={
          <ClinicalDataRibbon
            clinicalData={(note?.clinicalData ?? null) as ClinicalDataPartial | null}
            onUpdate={onClinicalDataUpdate}
            isReadOnly={isReadOnly}
          />
        }
      />
      {sealOpen ? (
        <SealRitualDialog
          open={sealOpen}
          onOpenChange={setSealOpen}
          kind="note"
          onConfirm={() => {
            void onSealConfirm();
          }}
          isPending={submit.isPending}
        />
      ) : null}
    </>
  );
}
